import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { getArticle, articles } from "@/data/articles";

export default function Article() {
  const { slug } = useParams();
  const article = getArticle(slug);

  if (!article) {
    return (
      <main data-testid="article-not-found" className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-wine">Article not found</h1>
          <Link to="/journal" className="link-underline inline-flex items-center gap-2 mt-6 text-terracotta font-medium">
            <ArrowLeft size={16} /> Back to the Journal
          </Link>
        </div>
      </main>
    );
  }

  const next = articles.find((a) => a.slug !== slug);

  return (
    <main data-testid="article-page">
      <article className="pt-40 md:pt-52 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal>
            <Link to="/journal" data-testid="article-back" className="link-underline inline-flex items-center gap-2 text-terracotta font-medium text-sm">
              <ArrowLeft size={16} /> The Journal
            </Link>
            <p className="text-xs uppercase tracking-[0.2em] text-terracotta mt-10">
              {article.category} · {article.date}
            </p>
            <h1 className="font-serif font-light text-wine text-4xl md:text-5xl leading-[1.15] tracking-tight mt-6">
              {article.title}
            </h1>
          </Reveal>

          <div className="mt-12 space-y-7">
            {article.body.map((p, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <p className={`leading-relaxed ${i === 0 ? "text-xl md:text-2xl text-wine font-serif font-light" : "text-lg text-charcoal/80"}`}>
                  {p}
                </p>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-16 pt-10 border-t border-gold/40">
              <p className="font-serif italic text-xl text-terracotta">Motherhood Is Leadership™</p>
              <p className="mt-1 text-sm text-charcoal/60">The Thrive &amp; Mother Company™</p>
            </div>
          </Reveal>
        </div>
      </article>

      {/* NEXT + CTA */}
      <section className="bg-blush/30 py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {next && (
            <Link to={`/journal/${next.slug}`} data-testid="article-next" className="group block">
              <p className="text-xs uppercase tracking-[0.2em] text-terracotta mb-3">Keep reading</p>
              <h3 className="font-serif text-2xl md:text-3xl text-wine group-hover:text-terracotta transition-colors flex items-center gap-3">
                {next.title} <ArrowRight size={22} className="flex-none group-hover:translate-x-1 transition-transform" />
              </h3>
            </Link>
          )}
          <div className="mt-12">
            <Link
              to="/shop"
              data-testid="article-shop-cta"
              className="inline-flex items-center gap-3 rounded-full bg-terracotta text-cream px-8 py-4 text-sm font-medium tracking-wide hover:bg-wine transition-colors duration-300"
            >
              Get the Weekly Reset™ — $17 <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
